import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Event } from '../types/Event';
import { eventService } from '../services/eventService.ts';
import { settingsService, DEFAULT_FIELD_ORDER } from '../services/settingsService.ts';
import { Timestamp, GeoPoint, QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import EventForm from './EventForm.tsx';
import './EventList.css';
import { ImportDialog } from './ImportDialog.tsx';
import { classifyEventType, classifyEventTypeDetailed } from '../services/aiService.ts';
import { EventCategory, EVENT_TYPES, EVENT_CATEGORIES } from '../constants/eventTypes.ts';
import { toFirestoreTimestamp, toFirestoreTimestampArray, addHoursToTimestamp, standardizeEventTimestamps } from '../utils/timestampUtils.ts';

interface EventListProps {
  onCreateClick: () => void;
}

interface DateRange {
  start: Date | null;
  end: Date | null;
}

interface FieldFilter {
  field: string;
  value: string;
}

/** Canonical `event_type` plus optional `type` (e.g. AI classification) — both surface as "type" in the UI. */
function collectEventTypeStrings(event: Event): string[] {
  return [event.event_type, event.type]
    .filter((t) => t != null && String(t).trim() !== '')
    .map((t) => String(t).trim());
}

interface Host {
  id: string;
  name: string;
}

const INITIAL_EVENTS_PAGE_SIZE = 100;
const LOAD_MORE_EVENTS_PAGE_SIZE = 100;

export const EventList: React.FC<EventListProps> = ({ onCreateClick }) => {
  const location = useLocation();
  const reportLinkState = location.state as any;
  const hasReportLinkFilters = Boolean(
    reportLinkState && (reportLinkState.source || reportLinkState.creationDateStart || reportLinkState.creationDateEnd)
  );
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMoreEvents, setLoadingMoreEvents] = useState(false);
  const [hasMoreEvents, setHasMoreEvents] = useState(true);
  const [lastVisibleDoc, setLastVisibleDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  /** Empty = no type restriction (all types). */
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [typeFilterMenuOpen, setTypeFilterMenuOpen] = useState(false);
  const typeFilterMenuRef = useRef<HTMLDivElement | null>(null);
  const [visibility, setVisibility] = useState<'all' | 'public' | 'private'>('all');
  const [timeFilter, setTimeFilter] = useState<'all' | 'upcoming' | 'past'>('all');
  const [selectedEvents, setSelectedEvents] = useState<Set<string>>(new Set());
  const [dateRange, setDateRange] = useState<DateRange>({ start: null, end: null });
  const [creationDateRange, setCreationDateRange] = useState<DateRange>({ start: null, end: null });
  const [locationArea, setLocationArea] = useState('');
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [expandedImageUrl, setExpandedImageUrl] = useState<string | null>(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [batchFieldName, setBatchFieldName] = useState<string>('');
  const [batchFieldValue, setBatchFieldValue] = useState<string>('');
  const [batchFieldType, setBatchFieldType] = useState<'string' | 'number' | 'boolean' | 'timestamp' | 'array'>('string');
  const [isNewField, setIsNewField] = useState(false);
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());
  /** Editable row state; filtering uses `fieldFiltersApplied` after the user clicks Search. */
  const [fieldFiltersDraft, setFieldFiltersDraft] = useState<FieldFilter[]>([]);
  const [fieldFiltersApplied, setFieldFiltersApplied] = useState<FieldFilter[]>([]);
  /** Distinct type labels from a full `posts` scan (same fetch as global summary). */
  const [catalogTypeOptions, setCatalogTypeOptions] = useState<string[] | null>(null);
  /** Distinct field keys from a full `posts` scan (same fetch as global summary). */
  const [catalogFieldOptions, setCatalogFieldOptions] = useState<string[] | null>(null);
  const [visibleFields, setVisibleFields] = useState<Set<string>>(new Set());
  
  // Save visible fields to localStorage whenever they change
  const saveVisibleFields = useCallback((fields: Set<string>) => {
    try {
      const fieldsArray = Array.from(fields);
      localStorage.setItem('visibleFields', JSON.stringify(fieldsArray));
    } catch (error) {
      console.error('Error saving visible fields to localStorage:', error);
    }
  }, []);
  const [allFields, setAllFields] = useState<string[]>([]);
  const [fieldOrder, setFieldOrder] = useState<string[]>([]);
  const [draggedField, setDraggedField] = useState<string | null>(null);
  // Use ref to persist expanded state across rerenders
  const getInitialExpandedState = (): boolean => {
    const saved = localStorage.getItem('fieldSelectorExpanded');
    return saved === 'true';
  };
  const fieldSelectorExpandedRef = useRef<boolean>(getInitialExpandedState());
  
  const [isFieldSelectorExpanded, setIsFieldSelectorExpanded] = useState<boolean>(fieldSelectorExpandedRef.current);
  const visibleFieldsInitializedRef = useRef<boolean>(false);
  const [hosts, setHosts] = useState<Host[]>([]);
  const [globalUpcomingSummary, setGlobalUpcomingSummary] = useState({
    upcoming: 0,
    within24h: 0,
    within72h: 0,
  });
  const [globalUpcomingSummaryLoading, setGlobalUpcomingSummaryLoading] = useState(true);
  /** True after a successful `getAllEvents` load; used to avoid refetching the full catalog on every filter tweak. */
  const [fullCatalogLoaded, setFullCatalogLoaded] = useState(false);
  const autoSyncFullCatalogRef = useRef(false);

  const getEventDateTimes = (event: Event): number[] => {
    const raw = Array.isArray(event.startDateTime) ? event.startDateTime : [event.startDateTime];
    return raw
      .map((dt) => {
        if (!dt) return null;
        if (dt instanceof Timestamp) return dt.toDate().getTime();
        const parsed = new Date(dt as string).getTime();
        return Number.isNaN(parsed) ? null : parsed;
      })
      .filter((value): value is number => value !== null);
  };

  const getEventCreationTime = (event: Event): number | null => {
    const candidates: unknown[] = [event.createdAt, (event as any).date_scraped, (event as any).updatedAt];
    for (const candidate of candidates) {
      if (!candidate) continue;
      if (candidate instanceof Timestamp) {
        return candidate.toDate().getTime();
      }
      const parsed = new Date(String(candidate)).getTime();
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }
    return null;
  };

  const normalizeSearchValue = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    if (value instanceof Timestamp) return value.toDate().toISOString();
    if (Array.isArray(value)) {
      return value.map((item) => normalizeSearchValue(item)).join(' ');
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch (error) {
        return '';
      }
    }
    return String(value);
  };

  const getEventSearchText = (event: Event): string => {
    const coreFields = [
      event.title,
      event.description,
      event.place,
      event.address,
      event.city,
      event.neighborhood,
      event.location,
      event.source,
      event.user_name,
      event.hostID,
      event.event_type,
      event.type,
      event.category,
      event.event_url,
      event.poster_url,
      event.trailer_url,
    ];

    const allValues = [
      ...coreFields,
      ...Object.values(event),
    ];

    return allValues
      .map((value) => normalizeSearchValue(value))
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  };

  const formatDateTime = (datetime: unknown): string => {
    if (!datetime) return 'No date';
    
    if (Array.isArray(datetime)) {
      return datetime
        .filter(time => time !== null && time !== undefined) // Filter out null/undefined values
        .map(time => {
          try {
            // Try to convert to Firestore timestamp first
            const timestamp = toFirestoreTimestamp(time);
            if (timestamp) {
              const date = timestamp.toDate();
              return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}`;
            }
            
            // Fallback for non-timestamp objects with seconds
            if (time && typeof time.seconds !== 'undefined') {
              const date = new Date(time.seconds * 1000);
              return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}`;
            }
            
            return 'Invalid date';
          } catch (error) {
            console.log('Error formatting date in array:', error);
            return 'Error formatting date';
          }
        })
        .join('\n');
    }
    
    // Try to convert to Firestore timestamp
    const timestamp = toFirestoreTimestamp(datetime);
    if (timestamp) {
      const date = timestamp.toDate();
      return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      })}`;
    }
    
    // Original timestamp instance check
    if (datetime instanceof Timestamp) {
      const date = new Date(datetime.seconds * 1000);
      return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      })}`;
    }

    if (typeof datetime === 'string') {
      try {
        const date = new Date(datetime);
        return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { 
          hour: '2-digit', 
          minute: '2-digit' 
        })}`;
      } catch (error) {
        return datetime;
      }
    }
    
    return String(datetime);
  };

  const handleSearchSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    setSearchTerm(searchInput.trim());
    setFieldFiltersApplied(fieldFiltersDraft.map((f) => ({ field: f.field, value: f.value })));
    // Full `posts` fetch is driven by `needsFullCatalog` (see sync effect) so every filter runs on the whole DB.
  };

  useEffect(() => {
    if (!typeFilterMenuOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (typeFilterMenuRef.current && !typeFilterMenuRef.current.contains(e.target as Node)) {
        setTypeFilterMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [typeFilterMenuOpen]);

  // Handle location state from scraper reports/progress links ("View Events" from report table)
  useEffect(() => {
    const state = reportLinkState;
    if (state && (state.source || state.creationDateStart || state.creationDateEnd)) {
      // Apply source filter if provided
      if (state.source) {
        const mergeSource = (prev: FieldFilter[]) => {
          const filtered = prev.filter((f) => f.field !== 'source');
          return [...filtered, { field: 'source', value: state.source }];
        };
        setFieldFiltersDraft((prev) => mergeSource(prev));
        setFieldFiltersApplied((prev) => mergeSource(prev));
      }
      // Apply creation date range if provided
      if (state.creationDateStart || state.creationDateEnd) {
        setCreationDateRange({
          start: state.creationDateStart ? new Date(state.creationDateStart) : null,
          end: state.creationDateEnd ? new Date(state.creationDateEnd) : null
        });
      }
      // Show all events from this run (don't filter by upcoming only — scraped events may have past dates)
      setTimeFilter('all');
      // Full catalog load: handled by needsFullCatalog sync once field/date filters are applied.
      // Do not call history.replaceState to clear state: that drops location.state, flips hasReportLinkFilters
      // to false, and triggers a paginated reload that can replace the full list — then source/date filters show 0 rows.
    }
  }, [reportLinkState]);

  // Deep-link from Hosts dashboard: /admin?hostID=<id> applies a field filter on hostID
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hostFromUrl = params.get('hostID') || params.get('hostId');
    const mergeHost = (prev: FieldFilter[]) => {
      const withoutHost = prev.filter((f) => f.field !== 'hostID');
      if (!hostFromUrl) return withoutHost;
      return [...withoutHost, { field: 'hostID', value: hostFromUrl }];
    };
    setFieldFiltersDraft((prev) => mergeHost(prev));
    setFieldFiltersApplied((prev) => mergeHost(prev));
  }, [location.search]);

  useEffect(() => {
    if (!hasReportLinkFilters) {
      const params = new URLSearchParams(location.search);
      const hostFromUrl = params.get('hostID') || params.get('hostId');
      // hostID effect loads the full catalog; avoid a paginated load that would race and replace it.
      if (!hostFromUrl) {
        loadEvents({ reset: true });
      }
    }
    loadHosts();
    loadFieldOrder();
    loadGlobalUpcomingSummary();
  }, [hasReportLinkFilters, location.search]);

  // Save visible fields to localStorage whenever they change (after initialization)
  useEffect(() => {
    if (visibleFieldsInitializedRef.current) {
      saveVisibleFields(visibleFields);
    }
  }, [visibleFields, saveVisibleFields]);


  const loadFieldOrder = async () => {
    try {
      const order = await settingsService.getFieldOrder();
      setFieldOrder(order);
    } catch (error) {
      console.error('Error loading field order:', error);
      setFieldOrder(DEFAULT_FIELD_ORDER);
    }
  };

  useEffect(() => {
    if (events.length > 0) {
      // Get all unique field names from all events
      const fields = new Set<string>();
      events.forEach(event => {
        Object.keys(event).forEach(key => fields.add(key));
      });
      const fieldArray = Array.from(fields);
      setAllFields(fieldArray);
      
      // Initialize visible fields only once when events first load
      if (!visibleFieldsInitializedRef.current) {
        // First time initialization - load from localStorage and merge with available fields
        const merged = new Set<string>();
        
        // First, try to load saved fields from localStorage
        try {
          const saved = localStorage.getItem('visibleFields');
          if (saved) {
            const savedFields = JSON.parse(saved) as string[];
            savedFields.forEach(field => {
              // Only add saved fields that still exist in current events
              if (fields.has(field)) {
                merged.add(field);
              }
            });
          }
        } catch (error) {
          console.error('Error loading saved visible fields:', error);
        }
        
        // If no saved fields or very few saved fields, initialize with all non-vector fields
        if (merged.size === 0 || merged.size < fieldArray.length * 0.3) {
          fieldArray.forEach(field => {
            if (!field.toLowerCase().includes('vector')) {
              merged.add(field);
            }
          });
        }
        
        setVisibleFields(merged);
        saveVisibleFields(merged);
        visibleFieldsInitializedRef.current = true;
      } else {
        // After initialization, only add new fields and remove fields that no longer exist
        setVisibleFields(currentVisible => {
          const merged = new Set<string>(currentVisible);
          let changed = false;
          
          // Remove fields that no longer exist in events
          merged.forEach(field => {
            if (!fields.has(field)) {
              merged.delete(field);
              changed = true;
            }
          });
          
          // Add new fields that don't contain 'vector'
          fieldArray.forEach(field => {
            if (!field.toLowerCase().includes('vector') && !merged.has(field)) {
              // Auto-add new fields to be helpful
              merged.add(field);
              changed = true;
            }
          });
          
          if (changed) {
            saveVisibleFields(merged);
          }
          return merged;
        });
      }
      
      // Update field order if there are new fields
      setFieldOrder(currentOrder => {
        if (currentOrder.length === 0) {
          // If no order exists yet, use default order and merge with any new fields
          const defaultOrder = [...DEFAULT_FIELD_ORDER];
          const newFields = fieldArray.filter(field => !defaultOrder.includes(field));
          return [...defaultOrder, ...newFields];
        } else {
          // Merge new fields into existing order if they don't exist
          const newFields = fieldArray.filter(field => !currentOrder.includes(field));
          if (newFields.length > 0) {
            const updatedOrder = [...currentOrder, ...newFields];
            // Save updated order
            settingsService.saveFieldOrder(updatedOrder).catch(error => {
              console.error('Error saving field order:', error);
            });
            return updatedOrder;
          }
          return currentOrder;
        }
      });
    }
  }, [events, saveVisibleFields]);

  const loadEvents = async (
    { reset = true, forceAll = false }: { reset?: boolean; forceAll?: boolean } = {}
  ) => {
    const pageSize = reset ? INITIAL_EVENTS_PAGE_SIZE : LOAD_MORE_EVENTS_PAGE_SIZE;
    const cursor = reset ? null : lastVisibleDoc;

    if (reset) {
      setLoading(true);
    }

    try {
      if (forceAll) {
        // "View Events" from scraper reports needs a complete set so source/date filters
        // don't miss matches that are outside the currently loaded paginated window.
        const allData = await eventService.getAllEvents();
        setEvents(allData);
        setLastVisibleDoc(null);
        setHasMoreEvents(false);
        setFullCatalogLoaded(true);
      } else {
        const page = await eventService.getEventsPage(pageSize, cursor);
        setEvents((prev) => (reset ? page.events : [...prev, ...page.events]));
        setLastVisibleDoc(page.lastVisible);
        setHasMoreEvents(page.hasMore);
        if (reset) {
          setFullCatalogLoaded(false);
        }
      }
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      if (reset) {
        setLoading(false);
      } else {
        setLoadingMoreEvents(false);
      }
    }
  };

  const needsFullCatalog = useMemo(
    () =>
      searchTerm.trim() !== '' ||
      selectedTypes.length > 0 ||
      visibility !== 'all' ||
      timeFilter !== 'all' ||
      Boolean(dateRange.start) ||
      Boolean(dateRange.end) ||
      Boolean(creationDateRange.start) ||
      Boolean(creationDateRange.end) ||
      Boolean(locationArea.trim()) ||
      showDuplicates ||
      fieldFiltersApplied.some((filter) => {
        const f = filter.field.trim();
        const v = filter.value.trim();
        if (v === ':missing' || v === ':empty') return true;
        return f !== '' && v !== '';
      }),
    [
      searchTerm,
      selectedTypes,
      visibility,
      timeFilter,
      dateRange,
      creationDateRange,
      locationArea,
      showDuplicates,
      fieldFiltersApplied,
    ]
  );

  useEffect(() => {
    if (loadingMoreEvents || autoSyncFullCatalogRef.current) {
      return;
    }
    if (needsFullCatalog && !fullCatalogLoaded) {
      autoSyncFullCatalogRef.current = true;
      void loadEvents({ reset: true, forceAll: true }).finally(() => {
        autoSyncFullCatalogRef.current = false;
      });
      return;
    }
    if (!needsFullCatalog && fullCatalogLoaded && !loading) {
      autoSyncFullCatalogRef.current = true;
      void loadEvents({ reset: true, forceAll: false }).finally(() => {
        autoSyncFullCatalogRef.current = false;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadEvents is stable enough; listing deps would refetch loops
  }, [needsFullCatalog, fullCatalogLoaded, loading, loadingMoreEvents]);

  const loadGlobalUpcomingSummary = async () => {
    try {
      setGlobalUpcomingSummaryLoading(true);
      const allEvents = await eventService.getAllEvents();
      const now = Date.now();
      const next24h = now + 24 * 60 * 60 * 1000;
      const next72h = now + 72 * 60 * 60 * 1000;

      let upcoming = 0;
      let within24h = 0;
      let within72h = 0;

      allEvents.forEach((event) => {
        const times = getEventDateTimes(event);
        if (times.length === 0) return;

        const hasUpcoming = times.some((t) => t >= now);
        const has24h = times.some((t) => t >= now && t <= next24h);
        const has72h = times.some((t) => t >= now && t <= next72h);

        if (hasUpcoming) upcoming += 1;
        if (has24h) within24h += 1;
        if (has72h) within72h += 1;
      });

      setGlobalUpcomingSummary({ upcoming, within24h, within72h });

      const typeSet = new Set<string>();
      allEvents.forEach((event) => {
        collectEventTypeStrings(event).forEach((t) => typeSet.add(t));
      });
      setCatalogTypeOptions(
        Array.from(typeSet).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' })
        )
      );

      const fieldSet = new Set<string>();
      allEvents.forEach((event) => {
        Object.keys(event).forEach((key) => {
          if (!key.toLowerCase().includes('vector') && !key.startsWith('_')) {
            fieldSet.add(key);
          }
        });
      });
      setCatalogFieldOptions(
        Array.from(fieldSet).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' })
        )
      );
    } catch (error) {
      console.error('Error loading global upcoming summary:', error);
      setGlobalUpcomingSummary({ upcoming: 0, within24h: 0, within72h: 0 });
      setCatalogTypeOptions([]);
      setCatalogFieldOptions(null);
    } finally {
      setGlobalUpcomingSummaryLoading(false);
    }
  };

  const handleLoadMoreEvents = async () => {
    if (loadingMoreEvents || !hasMoreEvents) return;
    setLoadingMoreEvents(true);
    await loadEvents({ reset: false });
  };

  const loadHosts = async () => {
    try {
      const hostsData = await eventService.getAllHosts();
      setHosts(hostsData);
    } catch (error) {
      console.error('Error loading hosts:', error);
    }
  };

  const findDuplicateEvents = (events: Event[]) => {
    const nameCounts = events.reduce((acc, event) => {
      const name = event.title.toLowerCase();
      acc[name] = (acc[name] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return events.filter(event => 
      nameCounts[event.title.toLowerCase()] > 1
    );
  };

  const deleteOlderDuplicates = async () => {
    const duplicateGroups = events.reduce((acc, event) => {
      // Create a unique key combining title, time, and place
      const title = event.title.toLowerCase();
      const time = event.startDateTime?.[0]?.seconds || 0;
      const place = event.place?.toLowerCase() || '';
      const key = `${title}|${time}|${place}`;
      
      if (!acc[key]) acc[key] = [];
      acc[key].push(event);
      return acc;
    }, {} as Record<string, Event[]>);

    const eventsToDelete: Event[] = [];
    
    Object.values(duplicateGroups).forEach(group => {
      if (group.length > 1) {
        // Sort by creation time, newest first
        const sorted = [...group].sort((a, b) => {
          const aTime = a.createdAt instanceof Timestamp ? a.createdAt.seconds : new Date(a.createdAt as string).getTime();
          const bTime = b.createdAt instanceof Timestamp ? b.createdAt.seconds : new Date(b.createdAt as string).getTime();
          return bTime - aTime;
        });
        
        // Keep the newest, delete the rest
        eventsToDelete.push(...sorted.slice(1));
      }
    });

    if (eventsToDelete.length === 0) {
      alert('No duplicate events found');
      return;
    }

    if (window.confirm(`Delete ${eventsToDelete.length} older duplicate events? (Events are considered duplicates if they have the same title, time, and place)`)) {
      try {
        for (const event of eventsToDelete) {
          if (event.id) await eventService.deleteEvent(event.id);
        }
        await loadEvents(); // Refresh the list
        alert('Successfully deleted older duplicate events');
      } catch (error) {
        console.error('Error deleting duplicates:', error);
        alert('Error deleting some events');
      }
    }
  };

  const getLatestEventTime = (event: Event): number => {
    if (!event.startDateTime) return 0;
    
    if (Array.isArray(event.startDateTime)) {
      // Get the latest date from the array
      return Math.max(...event.startDateTime.map(dt => 
        dt instanceof Timestamp 
          ? dt.seconds * 1000 
          : new Date(dt as string).getTime()
      ));
    }
    
    return event.startDateTime instanceof Timestamp
      ? event.startDateTime.seconds * 1000
      : new Date(event.startDateTime as string).getTime();
  };

  // Field names for "Add Field Filter": full-catalog keys + defaults + current page (covers empty in-memory list).
  const getAvailableFields = (): string[] => {
    const fields = new Set<string>();
    DEFAULT_FIELD_ORDER.forEach((k) => fields.add(k));
    catalogFieldOptions?.forEach((k) => fields.add(k));
    events.forEach((event) => {
      Object.keys(event).forEach((key) => {
        if (!key.toLowerCase().includes('vector') && !key.startsWith('_')) {
          fields.add(key);
        }
      });
    });
    return Array.from(fields).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  };

  // Add field filter
  const addFieldFilter = () => {
    setFieldFiltersDraft([...fieldFiltersDraft, { field: '', value: '' }]);
  };

  // Remove field filter
  const removeFieldFilter = (index: number) => {
    const newFilters = [...fieldFiltersDraft];
    newFilters.splice(index, 1);
    setFieldFiltersDraft(newFilters);
  };

  // Update field filter
  const updateFieldFilter = (index: number, field: string, value: string) => {
    const newFilters = [...fieldFiltersDraft];
    newFilters[index] = { field, value };
    setFieldFiltersDraft(newFilters);
  };

  // Filter events based on all criteria
  const filteredEvents = events.filter(event => {
    // Existing filters
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();
    const normalizedLocationArea = locationArea.trim().toLowerCase();
    const searchableText = getEventSearchText(event);
    const searchTokens = normalizedSearchTerm.split(/\s+/).filter(Boolean);

    const matchesSearch =
      searchTokens.length === 0 ||
      searchTokens.every((token) => searchableText.includes(token));

    const locationFields = [
      event.place,
      event.address,
      event.city,
      event.neighborhood,
      event.location,
      event.source,
    ]
      .map((value) => normalizeSearchValue(value).toLowerCase())
      .filter(Boolean)
      .join(' ');

    const matchesLocationArea =
      normalizedLocationArea === '' ||
      locationFields.includes(normalizedLocationArea);

    const matchesType =
      selectedTypes.length === 0 ||
      collectEventTypeStrings(event).some((t) => selectedTypes.includes(t));
    
    const matchesVisibility = visibility === 'all' || 
      (visibility === 'public' ? event.isPublic : !event.isPublic);

    // Event date range filtering
    const matchesDateRange = (() => {
      if (!dateRange.start && !dateRange.end) return true;
      
      const startDate = dateRange.start ? new Date(dateRange.start).getTime() : 0;
      const endDate = dateRange.end ? new Date(dateRange.end).getTime() : Number.MAX_SAFE_INTEGER;
      
      const eventDates = Array.isArray(event.startDateTime) 
        ? event.startDateTime.map(dt => dt instanceof Timestamp ? dt.toDate().getTime() : new Date(dt).getTime())
        : [event.startDateTime instanceof Timestamp 
            ? event.startDateTime.toDate().getTime() 
            : new Date(event.startDateTime as string).getTime()];

      return eventDates.some(date => date >= startDate && date <= endDate);
    })();

    // Creation date range filtering
    const matchesCreationDate = (() => {
      if (!creationDateRange.start && !creationDateRange.end) return true;

      const startDate = creationDateRange.start ? new Date(creationDateRange.start).getTime() : 0;
      const endDate = creationDateRange.end ? new Date(creationDateRange.end).getTime() : Number.MAX_SAFE_INTEGER;
      const creationDate = getEventCreationTime(event);
      // Don't hard-exclude events that have no parseable creation timestamp.
      if (creationDate === null) return true;
      return creationDate >= startDate && creationDate <= endDate;
    })();

    // Time filter (upcoming/past)
    const matchesTimeFilter = (() => {
      if (timeFilter === 'all') return true;

      const now = new Date().getTime();
      const eventDates = Array.isArray(event.startDateTime)
        ? event.startDateTime.map(dt => dt instanceof Timestamp ? dt.toDate().getTime() : new Date(dt).getTime())
        : [event.startDateTime instanceof Timestamp 
            ? event.startDateTime.toDate().getTime() 
            : new Date(event.startDateTime as string).getTime()];

      // For upcoming events, at least one date should be in the future
      if (timeFilter === 'upcoming') {
        return eventDates.some(date => date >= now);
      }
      // For past events, all dates should be in the past
      else if (timeFilter === 'past') {
        return eventDates.every(date => date < now);
      }
      return true;
    })();

    // Check for duplicates if showDuplicates is true
    const matchesDuplicates = !showDuplicates || (() => {
      const title = event.title.toLowerCase();
      const time = event.startDateTime?.[0]?.seconds || 0;
      const place = event.place?.toLowerCase() || '';
      const key = `${title}|${time}|${place}`;
      
      const duplicateCount = events.filter(e => {
        const eTitle = e.title.toLowerCase();
        const eTime = e.startDateTime?.[0]?.seconds || 0;
        const ePlace = e.place?.toLowerCase() || '';
        const eKey = `${eTitle}|${eTime}|${ePlace}`;
        return eKey === key;
      }).length;
      
      return duplicateCount > 1;
    })();

    // Dynamic field filters
    const matchesFieldFilters = fieldFiltersApplied.every(filter => {
      if (!filter.field || !filter.value) return true;
      
      const eventValue = event[filter.field];

      // Special case for searching empty fields
      if (filter.value === ':empty') {
        // Check if field exists and is an empty string
        return eventValue !== undefined && eventValue !== null && 
               (eventValue === '' || (Array.isArray(eventValue) && eventValue.length === 0));
      }

      // Special case for searching missing fields
      if (filter.value === ':missing') {
        // Check if field is undefined or null
        return eventValue === undefined || eventValue === null;
      }

      // Field key `type` in the schema is optional; primary category lives on `event_type`.
      if (filter.field === 'type') {
        const q = filter.value.toLowerCase();
        const hay = collectEventTypeStrings(event).map((t) => t.toLowerCase());
        if (hay.length === 0) return false;
        return hay.some((t) => t.includes(q));
      }

      if (eventValue === undefined || eventValue === null) return false;

      // Host ID: exact match (used with ?hostID= deep links from Hosts dashboard)
      if (filter.field === 'hostID') {
        return String(eventValue) === filter.value;
      }

      // Source field: tolerant match for report-link navigation
      // (some sources can differ slightly, e.g. suffixes/prefixes across local/cloud variants).
      if (filter.field === 'source') {
        const normalize = (value: string) =>
          value.toLowerCase().trim().replace(/[^a-z0-9_]/g, '');
        const eventSource = normalize(String(eventValue));
        const filterSource = normalize(filter.value);
        return (
          eventSource === filterSource ||
          eventSource.includes(filterSource) ||
          filterSource.includes(eventSource)
        );
      }

      if (Array.isArray(eventValue)) {
        return eventValue.some(v => 
          String(v).toLowerCase().includes(filter.value.toLowerCase())
        );
      } else if (typeof eventValue === 'object') {
        return JSON.stringify(eventValue).toLowerCase().includes(filter.value.toLowerCase());
      } else {
        return String(eventValue).toLowerCase().includes(filter.value.toLowerCase());
      }
    });

    return matchesSearch && matchesLocationArea && matchesType && matchesVisibility && 
           matchesDateRange && matchesFieldFilters && matchesDuplicates &&
           matchesCreationDate && matchesTimeFilter;
  }).sort((a, b) => {
    // Sort based on selected criteria
    switch (sortBy) {
      case 'name':
        return sortOrder === 'asc' 
          ? (a.title || '').localeCompare(b.title || '')
          : (b.title || '').localeCompare(a.title || '');

      case 'startTime':
        const aTime = Array.isArray(a.startDateTime) && a.startDateTime[0] 
          ? a.startDateTime[0].seconds 
          : 0;
        const bTime = Array.isArray(b.startDateTime) && b.startDateTime[0]
          ? b.startDateTime[0].seconds
          : 0;
        return sortOrder === 'asc' ? aTime - bTime : bTime - aTime;

      case 'likes':
        return sortOrder === 'asc' 
          ? (a.likes || 0) - (b.likes || 0)
          : (b.likes || 0) - (a.likes || 0);

      case 'comments':
        return sortOrder === 'asc'
          ? (a.comments || 0) - (b.comments || 0)
          : (b.comments || 0) - (a.comments || 0);

      case 'shares':
        return sortOrder === 'asc'
          ? (a.shares || 0) - (b.shares || 0)
          : (b.shares || 0) - (a.shares || 0);

      case 'createdAt':
      default:
        const aCreated = a.createdAt instanceof Timestamp 
          ? a.createdAt.seconds 
          : typeof a.createdAt === 'string' 
            ? new Date(a.createdAt).getTime() / 1000 
            : 0;
        const bCreated = b.createdAt instanceof Timestamp 
          ? b.createdAt.seconds 
          : typeof b.createdAt === 'string' 
            ? new Date(b.createdAt).getTime() / 1000 
            : 0;
        return sortOrder === 'asc' ? aCreated - bCreated : bCreated - aCreated;
    }
  });

  const clearAllFilters = () => {
    setSearchTerm('');
    setSearchInput('');
    setSelectedTypes([]);
    setVisibility('all');
    setTimeFilter('all');
    setDateRange({ start: null, end: null });
    setCreationDateRange({ start: null, end: null });
    setLocationArea('');
    setShowDuplicates(false);
    setFieldFiltersDraft([]);
    setFieldFiltersApplied([]);
  };

  const uniqueEventTypes = useMemo(() => {
    const s = new Set<string>();
    EVENT_TYPES.forEach((et) => s.add(et.name));
    catalogTypeOptions?.forEach((t) => s.add(t));
    events.forEach((e) => {
      collectEventTypeStrings(e).forEach((t) => s.add(t));
    });
    return Array.from(s).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  }, [catalogTypeOptions, events]);

  /** Category (main) → subtype labels for the type filter dropdown; each subtype appears once. */
  const typeDropdownGroups = useMemo(() => {
    const catalogSet = new Set(uniqueEventTypes);
    const assigned = new Set<string>();
    const groups: { category: string; types: string[] }[] = [];

    EVENT_CATEGORIES.forEach((category) => {
      const row: string[] = [];
      EVENT_TYPES.forEach((t) => {
        if (!catalogSet.has(t.name) || assigned.has(t.name)) return;
        if (t.categories.includes(category)) {
          row.push(t.name);
          assigned.add(t.name);
        }
      });
      row.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
      if (row.length > 0) {
        groups.push({ category, types: row });
      }
    });

    const other = uniqueEventTypes.filter((n) => !assigned.has(n));
    if (other.length > 0) {
      groups.push({ category: 'Other', types: other });
    }

    return groups;
  }, [uniqueEventTypes]);

  const handleEdit = (event: Event) => {
    setEditingEvent(event);
  };

  const handleDelete = async (id?: string) => {
    if (!id) return;
    try {
      await eventService.deleteEvent(id);
      setEvents(events.filter(event => event.id !== id));
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  const handleSelectEvent = (eventId: string) => {
    const newSelected = new Set(selectedEvents);
    if (newSelected.has(eventId)) {
      newSelected.delete(eventId);
    } else {
      newSelected.add(eventId);
    }
    setSelectedEvents(newSelected);
  };

  const handleBatchAction = async (action: 'delete' | 'public' | 'private' | 'type' | 'ai-classify' | 'add-2-hours' | 'standardize-timestamps' | 'swap-coordinates') => {
    if (selectedEvents.size === 0) return;
    
    try {
      const selectedIds = Array.from(selectedEvents).map(String);
      console.log('Selected IDs:', selectedIds);
      
      switch (action) {
        case 'delete':
          for (const id of selectedIds) {
            await eventService.deleteEvent(id);
          }
          setEvents(events.filter(event => !selectedEvents.has(event.id!)));
          setSelectedEvents(new Set());
          break;
        
        case 'public':
          for (const id of selectedIds) {
            await eventService.updateEvent(id, { isPublic: true });
          }
          setEvents(events.map(event => 
            selectedEvents.has(event.id!) 
              ? { ...event, isPublic: true }
              : event
          ));
          break;

        case 'private':
          for (const id of selectedIds) {
            await eventService.updateEvent(id, { isPublic: false });
          }
          setEvents(events.map(event => 
            selectedEvents.has(event.id!) 
              ? { ...event, isPublic: false }
              : event
          ));
          break;

        case 'add-2-hours':
          console.log('Starting to add 2 hours to selected events');
          const updatedEvents: Event[] = [];
          
          for (const id of selectedIds) {
            console.log(`Processing event ID: ${id}`);
            const event = events.find(e => e.id === id);
            if (event) {
              console.log(`Event found: ${event.title}`);
              console.log('Original startDateTime:', event.startDateTime);
              console.log('Original endDateTime:', event.endDateTime);
              
              // Process startDateTime (could be an array or a single timestamp)
              let updatedStartDateTime;
              if (Array.isArray(event.startDateTime)) {
                console.log('startDateTime is an array');
                updatedStartDateTime = event.startDateTime.map(ts => addHoursToTimestamp(ts, 2));
                console.log('Updated startDateTime array:', updatedStartDateTime);
              } else {
                console.log('startDateTime is a single value');
                updatedStartDateTime = addHoursToTimestamp(event.startDateTime, 2);
                console.log('Updated startDateTime:', updatedStartDateTime);
              }
              
              // Process endDateTime (could be an array, a single timestamp, or null)
              let updatedEndDateTime;
              if (Array.isArray(event.endDateTime)) {
                console.log('endDateTime is an array');
                updatedEndDateTime = event.endDateTime.map(ts => 
                  ts ? addHoursToTimestamp(ts, 2) : null
                );
                console.log('Updated endDateTime array:', updatedEndDateTime);
              } else if (event.endDateTime !== null && event.endDateTime !== undefined) {
                console.log('endDateTime is a single value');
                updatedEndDateTime = addHoursToTimestamp(event.endDateTime, 2);
                console.log('Updated endDateTime:', updatedEndDateTime);
              } else {
                console.log('endDateTime is null or undefined');
                updatedEndDateTime = event.endDateTime;
              }
              
              // Update the event in Firestore
              console.log(`Updating event ${id} in Firestore`);
              await eventService.updateEvent(id, {
                startDateTime: updatedStartDateTime,
                endDateTime: updatedEndDateTime
              });
              
              // Store updated event to update UI
              updatedEvents.push({
                ...event,
                startDateTime: updatedStartDateTime,
                endDateTime: updatedEndDateTime
              });
              console.log(`Successfully processed event ${id}`);
            } else {
              console.log(`Event with ID ${id} not found in the state`);
            }
          }
          
          // Update the UI
          console.log(`Updating UI with ${updatedEvents.length} modified events`);
          setEvents(events.map(event => {
            const updatedEvent = updatedEvents.find(e => e.id === event.id);
            return updatedEvent || event;
          }));
          
          console.log('Complete: Added 2 hours to events');
          alert(`Added 2 hours to ${updatedEvents.length} events.`);
          break;

        case 'type':
          const newType = (document.getElementById('batchEventType') as HTMLSelectElement)?.value;
          if (!newType) {
            alert('Please select a new event type');
            return;
          }
          
          for (const id of selectedIds) {
            await eventService.updateEvent(id, { event_type: newType });
          }
          setEvents(events.map(event => 
            selectedEvents.has(event.id!) 
              ? { ...event, event_type: newType }
              : event
          ));
          break;

        case 'ai-classify':
          const selectedEventsData = events.filter(event => selectedEvents.has(event.id!));
          const BATCH_SIZE = 5; // Process 5 events at a time
          const MAX_RETRIES = 3;
          const BASE_DELAY = 2000; // 2 seconds base delay
          const BATCH_DELAY = 5000; // 5 seconds between batches
          let failedEvents = 0;
          let successfulEvents = 0;

          // Split events into batches
          const batches: Event[][] = [];
          for (let i = 0; i < selectedEventsData.length; i += BATCH_SIZE) {
            batches.push(selectedEventsData.slice(i, i + BATCH_SIZE));
          }

          console.log(`Processing ${selectedEventsData.length} events in ${batches.length} batches`);

          // Process each batch
          for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
            const currentBatch = batches[batchIndex];
            console.log(`Starting batch ${batchIndex + 1}/${batches.length} with ${currentBatch.length} events`);

            // Process events in the current batch concurrently
            const batchResults = await Promise.allSettled(
              currentBatch.map(async (event) => {
                let retryCount = 0;
                
                while (retryCount < MAX_RETRIES) {
                  try {
                    if (retryCount > 0) {
                      const delay = BASE_DELAY * Math.pow(2, retryCount - 1);
                      console.log(`Waiting ${delay}ms before retry ${retryCount + 1} for event: "${event.title}"`);
                      await new Promise(resolve => setTimeout(resolve, delay));
                    }

                    console.log(`Processing event: "${event.title}" (Attempt ${retryCount + 1}/${MAX_RETRIES})`);

                    // Use a shorter description to reduce load on the AI service
                    const shortDescription = event.description
                      .split('\n')
                      .slice(0, 3)
                      .join('\n')
                      .slice(0, 500)
                      .trim();

                    const response = await fetch('https://us-central1-spontane-gog47y.cloudfunctions.net/classifyEventTypeDetailed', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                      },
                      body: JSON.stringify({
                        title: event.title,
                        description: shortDescription
                      })
                    });

                    if (response.status === 500) {
                      throw new Error('AI service temporarily unavailable');
                    }

                    if (!response.ok) {
                      throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    const result = await response.json();
                    console.log(`✨ AI classified "${event.title}" as: ${result.type} (${result.category})`);

                    await eventService.updateEvent(event.id!, { 
                      type: result.type,
                      category: result.category
                    });

                    setEvents(prevEvents => 
                      prevEvents.map(e => 
                        e.id === event.id 
                          ? { ...e, type: result.type, category: result.category }
                          : e
                      )
                    );

                    successfulEvents++;
                    return { success: true, event };
                  } catch (error) {
                    console.error(`Error classifying event "${event.title}" (Attempt ${retryCount + 1}):`, error);
                    retryCount++;

                    if (retryCount === MAX_RETRIES) {
                      console.warn(`Max retries reached for "${event.title}", using fallback classification`);
                      const fallbackType = inferEventCategory(event.title);
                      
                      try {
                        await eventService.updateEvent(event.id!, { 
                          type: fallbackType.type,
                          category: fallbackType.category
                        });

                        setEvents(prevEvents => 
                          prevEvents.map(e => 
                            e.id === event.id 
                              ? { ...e, type: fallbackType.type, category: fallbackType.category }
                              : e
                          )
                        );

                        failedEvents++;
                        return { success: false, event, fallback: true };
                      } catch (updateError) {
                        console.error(`Error updating event with fallback classification:`, updateError);
                        return { success: false, event, fallback: false };
                      }
                    }
                  }
                }
              })
            );

            // Log batch results
            const batchSuccesses = batchResults.filter(result => result.status === 'fulfilled' && result.value?.success).length;
            const batchFallbacks = batchResults.filter(result => result.status === 'fulfilled' && !result.value?.success && result.value?.fallback).length;
            const batchFailures = batchResults.filter(result => result.status === 'rejected' || (result.status === 'fulfilled' && !result.value?.success && !result.value?.fallback)).length;

            console.log(`Batch ${batchIndex + 1} results:`, {
              successful: batchSuccesses,
              fallbacks: batchFallbacks,
              failed: batchFailures
            });

            // Wait before processing next batch
            if (batchIndex < batches.length - 1) {
              console.log(`Waiting ${BATCH_DELAY}ms before starting next batch...`);
              await new Promise(resolve => setTimeout(resolve, BATCH_DELAY));
            }
          }

          console.log('Classification completed:', {
            total: selectedEventsData.length,
            successful: successfulEvents,
            failed: failedEvents
          });
          break;

        case 'standardize-timestamps':
          console.log('Starting to standardize timestamps for selected events');
          const standardizedEvents: Event[] = [];
          
          for (const id of selectedIds) {
            console.log(`Processing event ID: ${id}`);
            const event = events.find(e => e.id === id);
            if (event) {
              console.log(`Event found: ${event.title}`);
              console.log('Original startDateTime:', event.startDateTime);
              console.log('Original endDateTime:', event.endDateTime);
              
              // Standardize all timestamps in the event
              const standardizedEvent = standardizeEventTimestamps(event);
              
              console.log('Standardized startDateTime:', standardizedEvent.startDateTime);
              console.log('Standardized endDateTime:', standardizedEvent.endDateTime);
              
              // Update the event in Firestore
              console.log(`Updating event ${id} in Firestore`);
              await eventService.updateEvent(id, {
                startDateTime: standardizedEvent.startDateTime,
                endDateTime: standardizedEvent.endDateTime,
                createdAt: standardizedEvent.createdAt
              });
              
              // Store updated event to update UI
              standardizedEvents.push(standardizedEvent);
              console.log(`Successfully processed event ${id}`);
            } else {
              console.log(`Event with ID ${id} not found in the state`);
            }
          }
          
          // Update the UI
          console.log(`Updating UI with ${standardizedEvents.length} modified events`);
          setEvents(events.map(event => {
            const updatedEvent = standardizedEvents.find(e => e.id === event.id);
            return updatedEvent || event;
          }));
          
          console.log('Complete: Standardized timestamps for events');
          alert(`Converted timestamps to Firestore format for ${standardizedEvents.length} events.`);
          break;

        case 'swap-coordinates':
          console.log('Starting to swap coordinates for selected events');
          const eventsWithSwappedCoords: Event[] = [];
          
          for (const id of selectedIds) {
            console.log(`Processing event ID: ${id}`);
            const event = events.find(e => e.id === id);
            if (event && event.lat_long) {
              console.log(`Event found: ${event.title}`);
              console.log('Original coordinates:', event.lat_long);
              
              // Create a new GeoPoint with swapped coordinates
              const swappedCoordinates = new GeoPoint(
                event.lat_long.longitude,
                event.lat_long.latitude
              );
              
              console.log('Swapped coordinates:', swappedCoordinates);
              
              // Update the event in Firestore
              console.log(`Updating event ${id} in Firestore`);
              await eventService.updateEvent(id, {
                lat_long: swappedCoordinates
              });
              
              // Store updated event to update UI
              eventsWithSwappedCoords.push({
                ...event,
                lat_long: swappedCoordinates
              });
              console.log(`Successfully processed event ${id}`);
            } else {
              console.log(`Event with ID ${id} not found or has no coordinates`);
            }
          }
          
          // Update the UI
          console.log(`Updating UI with ${eventsWithSwappedCoords.length} modified events`);
          setEvents(events.map(event => {
            const updatedEvent = eventsWithSwappedCoords.find(e => e.id === event.id);
            return updatedEvent || event;
          }));
          
          console.log('Complete: Swapped coordinates for events');
          alert(`Swapped coordinates for ${eventsWithSwappedCoords.length} events.`);
          break;
      }
    } catch (error) {
      console.error('Error performing batch action:', error);
      alert('Error performing batch action');
    }
  };

  const handleSelectAll = () => {
    const filteredIds = new Set(filteredEvents.map(event => event.id!));
    if (filteredIds.size === selectedEvents.size && 
        Array.from(filteredIds).every(id => selectedEvents.has(id))) {
      // If all filtered events are selected, deselect them
      setSelectedEvents(new Set(
        Array.from(selectedEvents).filter(id => !filteredIds.has(id))
      ));
    } else {
      // Select all filtered events
      setSelectedEvents(new Set([
        ...Array.from(selectedEvents),
        ...Array.from(filteredIds)
      ]));
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    // TODO: Implement file upload logic
    console.log('File selected:', e.target.files?.[0]);
  };

  const handleEditSubmit = () => {
    setEditingEvent(null);
    loadEvents(); // Reload events to show updated data
  };

  const handleBatchFieldUpdate = async () => {
    if (!batchFieldName || selectedEvents.size === 0) return;

    let processedValue: any = batchFieldValue;
    
    // Process value based on type
    switch (batchFieldType) {
      case 'number':
        processedValue = Number(batchFieldValue);
        break;
      case 'boolean':
        processedValue = batchFieldValue.toLowerCase() === 'true';
        break;
      case 'timestamp':
        processedValue = Timestamp.fromDate(new Date(batchFieldValue));
        break;
      case 'array':
        try {
          processedValue = batchFieldValue.split(',').map(item => item.trim());
        } catch (e) {
          console.error('Error parsing array:', e);
          return;
        }
        break;
    }

    // Update each selected event
    for (const eventId of selectedEvents) {
      const event = events.find(e => e.id === eventId);
      if (event) {
        await eventService.updateEvent(eventId, {
          [batchFieldName]: processedValue
        });
      }
    }

    // Refresh events list
    loadEvents();
  };

  const toggleExpand = (eventId: string, fieldName: string) => {
    const key = `${eventId}-${fieldName}`;
    const newExpanded = new Set(expandedFields);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedFields(newExpanded);
  };

  const toggleFieldSelector = useCallback((expanded: boolean) => {
    fieldSelectorExpandedRef.current = expanded;
    setIsFieldSelectorExpanded(expanded);
    // Save to localStorage
    localStorage.setItem('fieldSelectorExpanded', String(expanded));
  }, []);
  

  const FieldSelector = useMemo(() => {
    // Use state value for rendering, but ref ensures it persists
    const isExpanded = isFieldSelectorExpanded;

    const handleFieldToggle = (field: string) => {
      const newVisibleFields = new Set(visibleFields);
      if (newVisibleFields.has(field)) {
        newVisibleFields.delete(field);
      } else {
        newVisibleFields.add(field);
      }
      setVisibleFields(newVisibleFields);
      saveVisibleFields(newVisibleFields);
    };

    const handleSelectAll = () => {
      const allFieldsSet = new Set(allFields);
      setVisibleFields(allFieldsSet);
      saveVisibleFields(allFieldsSet);
    };

    const handleSelectNone = () => {
      const emptySet = new Set<string>();
      setVisibleFields(emptySet);
      saveVisibleFields(emptySet);
    };

    const handleDragStart = (e: React.DragEvent, field: string) => {
      setDraggedField(field);
      e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = async (e: React.DragEvent, targetField: string) => {
      e.preventDefault();
      if (!draggedField || draggedField === targetField) return;

      const currentOrder = [...fieldOrder];
      const draggedIndex = currentOrder.indexOf(draggedField);
      const targetIndex = currentOrder.indexOf(targetField);

      if (draggedIndex === -1 || targetIndex === -1) {
        // If field not in order, add it
        if (draggedIndex === -1) {
          currentOrder.push(draggedField);
        }
        if (targetIndex === -1) {
          const newIndex = currentOrder.indexOf(draggedField);
          currentOrder.splice(newIndex, 1);
          currentOrder.splice(targetIndex === -1 ? currentOrder.length : targetIndex, 0, draggedField);
        }
      } else {
        // Reorder existing fields
        currentOrder.splice(draggedIndex, 1);
        currentOrder.splice(targetIndex, 0, draggedField);
      }

      setFieldOrder(currentOrder);
      setDraggedField(null);
      
      // Save to Firestore
      try {
        await settingsService.saveFieldOrder(currentOrder);
      } catch (error) {
        console.error('Error saving field order:', error);
      }
    };

    const handleMoveUp = async (field: string) => {
      const currentOrder = [...fieldOrder];
      const index = currentOrder.indexOf(field);
      if (index > 0) {
        [currentOrder[index - 1], currentOrder[index]] = [currentOrder[index], currentOrder[index - 1]];
        setFieldOrder(currentOrder);
        try {
          await settingsService.saveFieldOrder(currentOrder);
        } catch (error) {
          console.error('Error saving field order:', error);
        }
      }
    };

    const handleMoveDown = async (field: string) => {
      const currentOrder = [...fieldOrder];
      const index = currentOrder.indexOf(field);
      if (index < currentOrder.length - 1) {
        [currentOrder[index], currentOrder[index + 1]] = [currentOrder[index + 1], currentOrder[index]];
        setFieldOrder(currentOrder);
        try {
          await settingsService.saveFieldOrder(currentOrder);
        } catch (error) {
          console.error('Error saving field order:', error);
        }
      }
    };

    // Get ordered fields: fields in order first, then any remaining fields
    const getOrderedFields = () => {
      const ordered = fieldOrder.filter(field => allFields.includes(field));
      const unordered = allFields.filter(field => !fieldOrder.includes(field));
      return [...ordered, ...unordered];
    };

    const orderedFields = getOrderedFields();

    return (
      <div className="field-selector" style={{
        border: '1px solid #ddd',
        borderRadius: '4px',
        padding: '1rem',
        marginBottom: '1rem'
      }}>
        <div 
          className="field-selector-header" 
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: isExpanded ? '1rem' : '0',
            userSelect: 'none'
          }}
        >
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              flex: 1
            }}
          >
            <span 
              onClick={() => toggleFieldSelector(!isExpanded)}
              style={{ 
                fontSize: '1.2rem',
                cursor: 'pointer',
                userSelect: 'none',
                padding: '0.25rem'
              }}
            >
              {isExpanded ? '▼' : '▶'}
            </span>
            <h3 style={{ margin: 0 }}>Visible Fields {isExpanded && '(Drag to reorder)'}</h3>
          </div>
          {isExpanded && (
            <div 
              className="field-selector-actions" 
              onClick={(e) => e.stopPropagation()}
              style={{
                display: 'flex',
                gap: '0.5rem'
              }}
            >
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectAll();
                }}
                style={{
                  padding: '0.25rem 0.5rem',
                  backgroundColor: '#f0f0f0',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Select All
              </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectNone();
                }}
                style={{
                  padding: '0.25rem 0.5rem',
                  backgroundColor: '#f0f0f0',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Select None
              </button>
            </div>
          )}
        </div>
        {isExpanded && (
          <div 
            className="field-selector-list" 
            onClick={(e) => e.stopPropagation()}
            onDragStart={(e) => e.stopPropagation()}
            onDragOver={(e) => e.stopPropagation()}
            onDrop={(e) => e.stopPropagation()}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.25rem'
            }}
          >
          {orderedFields.map((field, index) => (
            <div
              key={`field-checkbox-${field}`}
              draggable
              onDragStart={(e) => {
                e.stopPropagation();
                handleDragStart(e, field);
              }}
              onDragOver={(e) => {
                e.stopPropagation();
                handleDragOver(e);
              }}
              onDrop={(e) => {
                e.stopPropagation();
                handleDrop(e, field);
              }}
              onClick={(e) => e.stopPropagation()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem',
                cursor: 'move',
                backgroundColor: draggedField === field ? '#e3f2fd' : 'transparent',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            >
              <span style={{ cursor: 'grab', userSelect: 'none' }}>☰</span>
              <label 
                className="field-checkbox" 
                onClick={(e) => e.stopPropagation()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  flex: 1,
                  cursor: 'pointer'
                }}
              >
                <input
                  type="checkbox"
                  checked={visibleFields.has(field)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleFieldToggle(field);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
                {field.replace(/([A-Z])/g, ' $1')
                  .replace(/_/g, ' ')
                  .trim()
                  .split(' ')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                  .join(' ')}
              </label>
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMoveUp(field);
                  }}
                  disabled={index === 0}
                  style={{
                    padding: '0.25rem 0.5rem',
                    backgroundColor: index === 0 ? '#f0f0f0' : '#e3f2fd',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: index === 0 ? 'not-allowed' : 'pointer',
                    opacity: index === 0 ? 0.5 : 1
                  }}
                  title="Move up"
                >
                  ↑
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMoveDown(field);
                  }}
                  disabled={index === orderedFields.length - 1}
                  style={{
                    padding: '0.25rem 0.5rem',
                    backgroundColor: index === orderedFields.length - 1 ? '#f0f0f0' : '#e3f2fd',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: index === orderedFields.length - 1 ? 'not-allowed' : 'pointer',
                    opacity: index === orderedFields.length - 1 ? 0.5 : 1
                  }}
                  title="Move down"
                >
                  ↓
                </button>
              </div>
            </div>
          ))}
          </div>
        )}
      </div>
    );
  }, [isFieldSelectorExpanded, allFields, fieldOrder, draggedField, toggleFieldSelector, visibleFields, setVisibleFields, setFieldOrder, setDraggedField, saveVisibleFields]);

  const handleDeleteEmptyFields = async () => {
    if (selectedEvents.size === 0 || !fieldFiltersApplied.length) return;
    
    const fieldsToDelete = fieldFiltersApplied
      .filter(filter => filter.value === ':empty')
      .map(filter => filter.field);

    if (fieldsToDelete.length === 0) {
      alert('Please add a field filter with ":empty" to specify which empty fields to delete');
      return;
    }

    if (window.confirm(`Delete these empty fields: ${fieldsToDelete.join(', ')} from ${selectedEvents.size} events?`)) {
      for (const eventId of selectedEvents) {
        const event = events.find(e => e.id === eventId);
        if (event) {
          const updates = fieldsToDelete.reduce((acc, field) => {
            if (event[field] !== undefined && (event[field] === '' || (Array.isArray(event[field]) && event[field].length === 0))) {
              acc[field] = null;
            }
            return acc;
          }, {});
          
          if (Object.keys(updates).length > 0) {
            await eventService.updateEvent(eventId, updates);
          }
        }
      }
      loadEvents(); // Refresh the list
    }
  };

  // Add the inferEventCategory helper function
  const inferEventCategory = (title: string): { category: EventCategory; type: string } => {
    const titleLower = title.toLowerCase();
    
    if (titleLower.includes('concert') || titleLower.includes('music') || titleLower.includes('live')) {
      return {
        category: 'Live Show & Music',
        type: 'Concerts & Gigs'
      };
    }
    if (titleLower.includes('party') || titleLower.includes('club')) {
      return {
        category: 'Party',
        type: 'Clubbing'
      };
    }
    if (titleLower.includes('art') || titleLower.includes('exhibition') || titleLower.includes('gallery')) {
      return {
        category: 'Art & Exhibitions',
        type: 'Exhibitions & Galleries'
      };
    }
    if (titleLower.includes('food') || titleLower.includes('dinner') || titleLower.includes('restaurant')) {
      return {
        category: 'Food & Drink',
        type: 'Special Nights & Menus'
      };
    }
    if (titleLower.includes('market') || titleLower.includes('fair')) {
      return {
        category: 'Markets & Pop-Ups',
        type: 'Pop-up Market'
      };
    }
    
    // Default fallback
    return {
      category: 'Social Meetups',
      type: 'Casual Hangouts'
    };
  };

  if (loading) return <div>Loading...</div>;

  const styles = {
    '.review-reason': {
      backgroundColor: '#fff3f3',
      padding: '12px',
      marginBottom: '16px',
      borderRadius: '8px',
      border: '1px solid #ffcdd2',
    },
    '.review-item': {
      marginTop: '8px',
    },
    '.review-item strong': {
      color: '#d32f2f',
      display: 'block',
      marginBottom: '4px',
    },
    '.review-item ul': {
      margin: '0',
      paddingLeft: '20px',
    },
    '.review-item li': {
      marginBottom: '4px',
      color: '#666',
    },
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-top">
        <h1 className="dashboard-title">Event Management Dashboard</h1>
        <div className="dashboard-actions">
          <button className="action-button primary" onClick={onCreateClick}>
            <span className="icon">+</span>
            Create New Event
          </button>
          {showImportDialog && (
            <ImportDialog onClose={() => setShowImportDialog(false)} />
          )}
          <button 
            className="action-button secondary" 
            onClick={() => setShowImportDialog(true)}
          >
            <span className="icon">📄</span>
            Import Events
          </button>
        </div>
      </div>

      <div className="dashboard-header">
        <div className="filters-container">
          {/* Search and Sort Row */}
          <div className="filters-row search-and-sort">
            <div className="search-block" style={{ flex: '1 1 720px', maxWidth: '800px' }}>
              <form className="search-section" onSubmit={handleSearchSubmit}>
                <input
                  type="text"
                  placeholder="Search events..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="search-input"
                />
                <button type="submit" className="action-button secondary search-button">
                  Search
                </button>
              </form>
              <p className="search-field-filters-hint" style={{ margin: '6px 0 0', fontSize: '12px', color: '#666' }}>
                Search applies the text box and every <strong>Add Field Filter</strong> row (not the other dropdowns—they filter as you change them).
              </p>
            </div>

            <div className="sort-group">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="select-filter"
              >
                <option value="createdAt">Creation Time</option>
                <option value="name">Name</option>
                <option value="startTime">Start Time</option>
                <option value="likes">Likes</option>
                <option value="comments">Comments</option>
                <option value="shares">Shares</option>
              </select>
              <button
                className="sort-order-button"
                onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>

          {/* Main Filters Row */}
          <div className="filters-row">
            <div className="type-multi-filter" ref={typeFilterMenuRef}>
              <button
                type="button"
                className="select-filter type-multi-trigger"
                onClick={() => setTypeFilterMenuOpen((o) => !o)}
                aria-expanded={typeFilterMenuOpen}
                aria-haspopup="listbox"
              >
                {selectedTypes.length === 0
                  ? 'All types'
                  : `${selectedTypes.length} type${selectedTypes.length === 1 ? '' : 's'}`}
              </button>
              {typeFilterMenuOpen && (
                <div className="type-multi-dropdown" role="listbox">
                  <label className="type-multi-option">
                    <input
                      type="checkbox"
                      checked={selectedTypes.length === 0}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedTypes([]);
                      }}
                    />
                    <span>All types</span>
                  </label>
                  {typeDropdownGroups.map(({ category, types }) => (
                    <div key={`type-group-${category}`} className="type-multi-group">
                      <div className="type-multi-group-heading">{category}</div>
                      {types.map((type) => (
                        <label key={`event-type-${type}`} className="type-multi-option type-multi-option-sub">
                          <input
                            type="checkbox"
                            checked={selectedTypes.includes(type)}
                            onChange={() => {
                              setSelectedTypes((prev) =>
                                prev.includes(type)
                                  ? prev.filter((t) => t !== type)
                                  : [...prev, type].sort((a, b) =>
                                      a.localeCompare(b, undefined, { sensitivity: 'base' })
                                    )
                              );
                            }}
                          />
                          <span>{type}</span>
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <select
              value={visibility}
              onChange={(e) => setVisibility(e.target.value as 'all' | 'public' | 'private')}
              className="select-filter"
            >
              <option value="all">All Events</option>
              <option value="public">Public Only</option>
              <option value="private">Private Only</option>
            </select>

            <select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value as 'all' | 'upcoming' | 'past')}
              className="select-filter"
            >
              <option value="all">All Times</option>
              <option value="upcoming">Upcoming Events</option>
              <option value="past">Past Events</option>
            </select>

            <div className="date-range">
              <span>Event Date Range:</span>
              <input
                type="date"
                value={dateRange.start ? dateRange.start.toISOString().split('T')[0] : ''}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value ? new Date(e.target.value) : null }))}
                className="date-input"
              />
              <span>to</span>
              <input
                type="date"
                value={dateRange.end ? dateRange.end.toISOString().split('T')[0] : ''}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value ? new Date(e.target.value) : null }))}
                className="date-input"
              />
            </div>

            <div className="date-range">
              <span>Creation Date Range:</span>
              <input
                type="date"
                value={creationDateRange.start ? creationDateRange.start.toISOString().split('T')[0] : ''}
                onChange={(e) => setCreationDateRange(prev => ({ ...prev, start: e.target.value ? new Date(e.target.value) : null }))}
                className="date-input"
              />
              <span>to</span>
              <input
                type="date"
                value={creationDateRange.end ? creationDateRange.end.toISOString().split('T')[0] : ''}
                onChange={(e) => setCreationDateRange(prev => ({ ...prev, end: e.target.value ? new Date(e.target.value) : null }))}
                className="date-input"
              />
            </div>

            <input
              type="text"
              value={locationArea}
              onChange={(e) => setLocationArea(e.target.value)}
              placeholder="Filter by location..."
              className="location-input"
            />

            <label>
              <input
                type="checkbox"
                checked={showDuplicates}
                onChange={(e) => setShowDuplicates(e.target.checked)}
              />
              Show Duplicate Events
            </label>

            <label>
              <input
                type="checkbox"
                checked={fieldFiltersDraft.some(f => f.value === ':missing')}
                onChange={(e) => {
                  const sync = (rows: FieldFilter[]) => {
                    setFieldFiltersDraft(rows);
                    setFieldFiltersApplied(rows);
                  };
                  if (e.target.checked) {
                    const missingFilter = fieldFiltersDraft.find(f => f.value === ':missing');
                    if (!missingFilter) {
                      sync([...fieldFiltersDraft, { field: '', value: ':missing' }]);
                    }
                  } else {
                    sync(fieldFiltersDraft.filter(f => f.value !== ':missing'));
                  }
                }}
              />
              Show Events with Missing Fields
            </label>
          </div>

          {/* Field Filters Row */}
          <div className="filters-row field-filters">
            <div className="field-filters-container">
              {fieldFiltersDraft.map((filter, index) => (
                <div key={`field-filter-${index}`} className="field-filter-item">
                  <select
                    value={filter.field}
                    onChange={(e) => updateFieldFilter(index, e.target.value, filter.value)}
                    className="select-filter"
                  >
                    <option value="">Select Field</option>
                    {getAvailableFields().map(field => (
                      <option key={`visible-field-${field}`} value={field}>
                        {field}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={filter.value}
                    onChange={(e) => updateFieldFilter(index, filter.field, e.target.value)}
                    placeholder="Filter value or :empty"
                    className="field-filter-input"
                  />
                  <button
                    className="remove-filter-button"
                    onClick={() => removeFieldFilter(index)}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                className="add-filter-button"
                onClick={addFieldFilter}
              >
                Add Field Filter
              </button>
            </div>
          </div>

          {/* Actions Row */}
          <div className="filters-row actions">
            <div className="batch-actions">
              <button className="btn-select-all" onClick={handleSelectAll}>
                {selectedEvents.size === filteredEvents.length ? 'Deselect All' : 'Select All'}
              </button>
              {selectedEvents.size > 0 && fieldFiltersApplied.some(f => f.value === ':empty') && (
                <button 
                  className="btn-batch delete"
                  onClick={handleDeleteEmptyFields}
                  title="Delete empty fields from selected events"
                >
                  Delete Empty Fields
                </button>
              )}
              <button 
                className="btn-batch delete"
                onClick={deleteOlderDuplicates}
                title="Keep only the newest event for each duplicate title"
              >
                Delete Older Duplicates
              </button>
              {selectedEvents.size > 0 && (
                <>
                  <button className="btn-batch" onClick={() => handleBatchAction('public')}>
                    Make Selected Public
                  </button>
                  <button className="btn-batch" onClick={() => handleBatchAction('private')}>
                    Make Selected Private
                  </button>
                  <button className="btn-batch" onClick={() => handleBatchAction('ai-classify')}>
                    🤖 AI Classify Selected
                  </button>
                  <button 
                    onClick={() => handleBatchAction('delete')} 
                    disabled={selectedEvents.size === 0}
                    className="btn-batch delete"
                  >
                    Delete Selected
                  </button>
                  <button 
                    onClick={() => handleBatchAction('add-2-hours')} 
                    disabled={selectedEvents.size === 0}
                    className="action-btn"
                  >
                    Add 2 Hours to Times
                  </button>
                  <button 
                    onClick={() => handleBatchAction('standardize-timestamps')} 
                    disabled={selectedEvents.size === 0}
                    className="action-btn"
                    title="Convert string timestamps to proper Firestore Timestamps"
                  >
                    Standardize Timestamps
                  </button>
                  <button 
                    onClick={() => handleBatchAction('swap-coordinates')} 
                    disabled={selectedEvents.size === 0}
                    className="action-btn"
                    title="Swap latitude and longitude coordinates"
                  >
                    Swap Coordinates
                  </button>
                  <select 
                    id="batchEventType"
                    onChange={() => handleBatchAction('type')}
                    defaultValue=""
                  >
                    <option value="" disabled>Change Type To...</option>
                    {uniqueEventTypes.map(type => (
                      <option key={`batch-type-${type}`} value={type}>{type}</option>
                    ))}
                  </select>
                </>
              )}
            </div>

            {selectedEvents.size > 0 && (
              <div className="filters-row batch-field-edit">
                <div className="field-edit-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={isNewField}
                      onChange={(e) => setIsNewField(e.target.checked)}
                    />
                    Create New Field
                  </label>

                  {isNewField ? (
                    <input
                      type="text"
                      value={batchFieldName}
                      onChange={(e) => setBatchFieldName(e.target.value)}
                      placeholder="Enter new field name"
                      className="field-input"
                    />
                  ) : (
                    <select
                      value={batchFieldName}
                      onChange={(e) => {
                        setBatchFieldName(e.target.value);
                        // Reset field value and type when changing field
                        setBatchFieldValue('');
                        if (e.target.value === 'hostID') {
                          setBatchFieldType('string');
                        }
                      }}
                      className="select-filter"
                    >
                      <option value="">Select Field</option>
                      <option value="hostID">Host</option>
                      {allFields
                        .filter(field => field !== 'hostID') // Exclude hostID from regular fields
                        .map(field => (
                          <option key={`host-${field}`} value={field}>
                            {field.replace(/([A-Z])/g, ' $1')
                              .replace(/_/g, ' ')
                              .trim()
                              .split(' ')
                              .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                              .join(' ')}
                          </option>
                        ))}
                    </select>
                  )}

                  {batchFieldName === 'hostID' ? (
                    <select
                      value={batchFieldValue}
                      onChange={(e) => setBatchFieldValue(e.target.value)}
                      className="select-filter"
                    >
                      <option value="">Select Host</option>
                      {hosts.map(host => (
                        <option key={`host-${host.id}`} value={host.id}>
                          {host.name}
                        </option>
                      ))}
                    </select>
                  ) : batchFieldType === 'boolean' ? (
                    <select
                      value={batchFieldValue}
                      onChange={(e) => setBatchFieldValue(e.target.value)}
                      className="select-filter"
                    >
                      <option value="true">True</option>
                      <option value="false">False</option>
                    </select>
                  ) : batchFieldType === 'timestamp' ? (
                    <input
                      type="datetime-local"
                      value={batchFieldValue}
                      onChange={(e) => setBatchFieldValue(e.target.value)}
                      className="field-input"
                    />
                  ) : (
                    <input
                      type={batchFieldType === 'number' ? 'number' : 'text'}
                      value={batchFieldValue}
                      onChange={(e) => setBatchFieldValue(e.target.value)}
                      placeholder="Enter field value"
                      className="field-input"
                    />
                  )}

                  <button 
                    className="btn-batch"
                    onClick={handleBatchFieldUpdate}
                    disabled={!batchFieldName || !batchFieldValue}
                  >
                    Update Field
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="results-info-container">
        <table className="upcoming-summary-table">
          <thead>
            <tr>
              <th>All upcoming events</th>
              <th>Next 24h</th>
              <th>Next 72h</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{globalUpcomingSummaryLoading ? 'Loading...' : globalUpcomingSummary.upcoming}</td>
              <td>{globalUpcomingSummaryLoading ? 'Loading...' : globalUpcomingSummary.within24h}</td>
              <td>{globalUpcomingSummaryLoading ? 'Loading...' : globalUpcomingSummary.within72h}</td>
            </tr>
          </tbody>
        </table>
        <span className="results-count">
          {filteredEvents.length} {filteredEvents.length === 1 ? 'event' : 'events'} found
          {filteredEvents.length !== events.length && ` (filtered from ${events.length} total)`}
        </span>
        {filteredEvents.length === 0 && events.length > 0 && (
          <button className="clear-filters-inline-btn" onClick={clearAllFilters}>
            Clear all filters
          </button>
        )}
      </div>

      <div className="events-list">
        <div className="controls" key="field-selector-container">
          {FieldSelector}
        </div>
        {filteredEvents.map((event) => (
          <div key={event.id}>
            <div className="event-card">
              <div className="event-header">
                <div className="event-title-section">
                  <div className="title-row">
                    <input
                      type="checkbox"
                      checked={selectedEvents.has(event.id!)}
                      onChange={() => handleSelectEvent(event.id!)}
                      className="event-checkbox"
                    />
                    <h2>{event.title}</h2>
                  </div>
                  <div className="status-controls">
                    <button 
                      className={`visibility-toggle ${event.isPublic ? 'public' : 'private'}`}
                      onClick={() => handleBatchAction(event.isPublic ? 'private' : 'public')}
                    >
                      {event.isPublic ? 'Make Private' : 'Make Public'}
                    </button>
                    <span className={`status-tag ${event.isPublic ? 'public' : 'private'}`}>
                      {event.isPublic ? 'Public' : 'Private'}
                    </span>
                  </div>
                </div>
                <div className="event-actions">
                  <button className="btn-edit" onClick={() => handleEdit(event)}>
                    {editingEvent?.id === event.id ? 'Close' : 'Edit'}
                  </button>
                  <button className="btn-delete" onClick={() => handleDelete(event.id)}>
                    Delete
                  </button>
                </div>
              </div>

              <div className="event-content">
                <div className="event-main-info">
                  {event._reviewReason && (
                    <div className="info-section review-reason">
                      <h3>Review Required</h3>
                      {event._reviewReason.missingFields.length > 0 && (
                        <div className="review-item">
                          <strong>Missing Fields:</strong>
                          <ul>
                            {event._reviewReason.missingFields.map(field => (
                              <li key={`missing-${event.id}-${field}`}>{field}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {event._reviewReason.hasConflict && event._reviewReason.conflictingEvents && (
                        <div className="review-item">
                          <strong>Duplicate Events Found:</strong>
                          <ul>
                            {event._reviewReason.conflictingEvents.map((duplicate, index) => (
                              <li key={`duplicate-${event.id}-${index}`}>
                                "{duplicate.title}" at {duplicate.place} on {formatDateTime(duplicate.time)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="info-section">
                    <h3>Event Details</h3>
                    {Object.entries(event)
                      .filter(([key]) => visibleFields.has(key))
                      .sort(([a], [b]) => {
                        // Sort by field order
                        const aIndex = fieldOrder.indexOf(a);
                        const bIndex = fieldOrder.indexOf(b);
                        
                        // If both fields are in the order, sort by their position
                        if (aIndex !== -1 && bIndex !== -1) {
                          return aIndex - bIndex;
                        }
                        // If only one is in the order, prioritize it
                        if (aIndex !== -1) return -1;
                        if (bIndex !== -1) return 1;
                        // If neither is in the order, sort alphabetically
                        return a.localeCompare(b);
                      })
                      .map(([key, value]) => {
                        if (value === null || value === undefined) return null;

                        const fieldKey = `${event.id}-${key}`;
                        const isExpanded = expandedFields.has(fieldKey);
                        
                        const displayName = key
                          .replace(/([A-Z])/g, ' $1')
                          .replace(/_/g, ' ')
                          .trim()
                          .split(' ')
                          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                          .join(' ');

                        let displayValue: any;
                        let hasMore = false;

                        if (key === 'startDateTime' || key === 'endDateTime') {
                          displayValue = formatDateTime(value);
                        } else if (key === 'createdAt' || key === 'updatedAt') {
                          displayValue = formatDateTime(value);
                        } else if (key === 'lat_long') {
                          displayValue = `${value.latitude}, ${value.longitude}`;
                        } else if (Array.isArray(value)) {
                          displayValue = value.join(', ');
                        } else if (key === 'event_url' || key === 'poster_url' || key === 'trailer_url') {
                          if (value) {
                            return (
                              <div className="info-row" key={`${event.id}-${key}`}>
                                <strong>{displayName}:</strong>
                                <a
                                  href={value}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="event-link"
                                >
                                  {value}
                                </a>
                              </div>
                            );
                          } else {
                            displayValue = value;
                          }
                        } else if (key === 'description') {
                          hasMore = value.length > 200;
                          displayValue = isExpanded ? value : value.slice(0, 200);
                          if (!isExpanded && hasMore) {
                            displayValue += '...';
                          }
                        } else {
                          displayValue = String(value);
                        }

                        return (
                          <div className="info-row" key={`${event.id}-${key}`}>
                            <strong>{displayName}:</strong>
                            <div className="field-value-container">
                              {key === 'description' ? (
                                <p>{displayValue}</p>
                              ) : (
                                <span>{displayValue}</span>
                              )}
                              {hasMore && (
                                <button 
                                  className="expand-button"
                                  onClick={() => toggleExpand(event.id!, key)}
                                >
                                  {isExpanded ? 'Show Less' : 'Show More'}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>

                  <div className="info-section">
                    <h3>User Information</h3>
                    <div className="info-row">
                      <strong>User ID:</strong> {event.userId}
                    </div>
                    <div className="info-row">
                      <strong>User Name:</strong> {event.user_name}
                    </div>
                    <div className="info-row">
                      <strong>Host ID:</strong> {event.hostID || 'N/A'}
                    </div>
                  </div>

                  <div className="info-section">
                    <h3>Engagement</h3>
                    <div className="stats-container">
                      <div className="stat-item">
                        <span className="stat-value">{event.likes || 0}</span>
                        <span className="stat-label">Likes</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-value">{event.comments || 0}</span>
                        <span className="stat-label">Comments</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-value">{event.shares || 0}</span>
                        <span className="stat-label">Shares</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="event-meta">
                  <span>Created by {event.user_name}</span>
                  {event.id && <span> | ID: {event.id}</span>}
                </div>

                {event.Image_gallery_url && event.Image_gallery_url.length > 0 && (
                  <div className="event-images">
                    <h3>Images</h3>
                    <div className="image-gallery">
                      {event.Image_gallery_url.map((url, index) => (
                        <div key={`image-${event.id}-${index}`} className="image-thumbnail">
                          <img src={url} alt={`Event ${index + 1}`} onClick={() => setExpandedImageUrl(url)} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {editingEvent?.id === event.id && (
              <div className="edit-form-container">
                <EventForm 
                  event={editingEvent} 
                  onSubmit={handleEditSubmit}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {!loading && (
        <div className="load-more-container">
          {hasMoreEvents ? (
            <button
              className="action-button secondary"
              onClick={handleLoadMoreEvents}
              disabled={loadingMoreEvents}
            >
              {loadingMoreEvents ? 'Loading...' : 'Load More (100)'}
            </button>
          ) : (
            <span className="results-count">All loaded events are shown.</span>
          )}
        </div>
      )}

      {expandedImageUrl && (
        <div className="image-modal" onClick={() => setExpandedImageUrl(null)}>
          <div className="modal-content">
            <img src={expandedImageUrl} alt="Expanded view" />
            <button className="close-button" onClick={() => setExpandedImageUrl(null)}>×</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventList; 